import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import StoreProductSpecs from 'app/javascript/components/store/StoreProductSpecs';
import StoreSizePicker from 'app/javascript/components/store/StoreSizePicker';
import StoreRelatedProducts from 'app/javascript/components/store/StoreRelatedProducts';
import ProductBuyButton from 'app/javascript/components/store/ProductBuyButton';
import StoreEmptyState from 'app/javascript/components/store/StoreEmptyState';
import { Skeleton } from 'app/javascript/components/ui/skeleton';
import { useStoreProduct, useStoreCatalog } from 'app/javascript/queries/useStoreCatalog';
import { usePublicCompany } from 'app/javascript/queries/useCompanies';
import { trackEvent } from 'app/javascript/firebase';
import { ROUTES } from 'app/javascript/constants/routes';
import { buildWhatsAppUrl } from 'app/javascript/utils/whatsapp';

export default function CompanyStoreProductPage() {
  const { slug, productId } = useParams({ from: ROUTES.COMPANY_STORE_PRODUCT });

  const { data: product, isLoading, isError } = useStoreProduct(slug, productId);
  // Company fetch only supplies name/phone for the WhatsApp CTA; the product
  // renders without it and the CTA degrades gracefully on failure (R7).
  const { data: company, isLoading: companyLoading, isError: companyError } = usePublicCompany(slug, { retry: false });
  // Full catalog (cheap, static mock) powers the "produtos semelhantes" row.
  const { data: catalog } = useStoreCatalog(slug);

  const [size, setSize] = useState<string | null>(null);

  // Navigating to a related product reuses this component instance, so reset the
  // size selection when the product changes (otherwise the old size lingers).
  useEffect(() => {
    setSize(null);
  }, [productId]);

  // Fire once per product (keyed to the id) so re-navigating between products in
  // the same component instance re-tracks for the new product.
  const trackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!product || trackedRef.current === productId) return;
    trackedRef.current = productId;
    trackEvent('store_product_opened', { company_slug: slug, product: product.id, category: product.category });
  }, [product, productId, slug]);

  if (isLoading) return <ProductLoading slug={slug} />;
  if (isError || !product) return <StoreEmptyState slug={slug} />;

  const academyName = company?.name ?? 'academia';
  const phone = company?.phone ?? null;
  const ctaLoading = companyLoading && !companyError;
  const sizes = product.sizes ?? [];
  const specs = product.specs ?? [];
  const needsSize = sizes.length > 0 && !size;

  // Other products in the same category, capped so the row stays tidy.
  const relatedProducts =
    catalog?.products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4) ?? [];

  const message =
    `Olá! Vi o produto "${product.name}"${size ? ` (tamanho ${size})` : ''} na loja da ${academyName} e tenho interesse.`;
  const whatsappUrl = ctaLoading || needsSize ? null : buildWhatsAppUrl(phone, message);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
        <Link
          to={ROUTES.COMPANY_STORE}
          params={{ slug }}
          className="mb-6 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-yellow-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/50"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          Voltar para a loja
        </Link>

        <div className="grid gap-8 md:grid-cols-2 md:gap-12">
          <div className="aspect-square overflow-hidden rounded-2xl border border-slate-700/50 bg-white">
            <img
              src={product.image}
              alt={`${product.name} — ${academyName}`}
              className="h-full w-full object-contain"
            />
          </div>

          <div className="flex flex-col justify-center gap-6">
            <div>
              <h1 className="font-bebas text-3xl uppercase leading-tight tracking-wide text-foreground sm:text-4xl">
                {product.name}
              </h1>
              {product.variant && <p className="mt-1 text-sm text-slate-400">{product.variant}</p>}
              {product.description && (
                <p className="mt-3 text-sm leading-relaxed text-slate-300">{product.description}</p>
              )}
            </div>

            <StoreSizePicker sizes={sizes} value={size} onChange={setSize} />

            <ProductBuyButton
              product={product}
              whatsappUrl={whatsappUrl}
              loading={ctaLoading}
              companySlug={slug}
              source="detail"
              disabledReason={needsSize ? 'Selecione um tamanho' : null}
              className="sm:max-w-xs"
            />
          </div>
        </div>

        <div className="mt-12 sm:mt-16">
          <StoreProductSpecs specs={specs} />
        </div>

        <StoreRelatedProducts products={relatedProducts} academyName={academyName} companySlug={slug} />
      </div>
    </div>
  );
}

// Detail-page skeleton: keeps the back link and the two-column shape so the page
// doesn't jump when the product resolves.
function ProductLoading({ slug }: { slug: string }) {
  return (
    <div className="min-h-screen bg-background text-foreground" aria-busy="true" aria-label="Carregando produto">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
        <Link
          to={ROUTES.COMPANY_STORE}
          params={{ slug }}
          className="mb-6 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          Voltar para a loja
        </Link>

        <div className="grid gap-8 md:grid-cols-2 md:gap-12">
          <Skeleton className="aspect-square w-full rounded-2xl" />
          <div className="flex flex-col justify-center gap-4">
            <Skeleton className="h-9 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-11 w-full max-w-xs" />
          </div>
        </div>
        <div className="mt-12 space-y-5 sm:mt-16">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-40 w-full rounded-2xl" />
        </div>
      </div>
    </div>
  );
}
